import { forwardRef } from 'react'
import { cn } from '@shared/lib/cn'
import { ButtonSize, ButtonVariant } from './types'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
}

// Figma specs: 40px height, 12px radius (rounded-xl), 16px h-pad (px-4), 16px font (text-lg), gap-2
const variantClasses: Record<ButtonVariant, string> = {
  // Заливка градиентная, как в макете (722:28799): вертикальный переход
  // #F4821E → #F46D1E. Рамка остаётся брендовой — разница с верхней точкой
  // градиента 7/255 по одному каналу, на пиксельной рамке не читается.
  [ButtonVariant.primary]:
    'from-orange-gradient-top to-orange-gradient-bottom border-orange bg-gradient-to-b text-white hover:opacity-90',
  // Градиент и тень сняты с кнопки «Приступить к олимпиаде» (499:139099):
  // linear-gradient(#0DB459 36.25%, #0FA151 130%) + drop-shadow 0 4px 4px #0FA55329.
  [ButtonVariant.success]:
    'from-green to-green-deep border-green bg-gradient-to-b text-white shadow-[0_4px_4px_rgba(15,165,83,0.16)] hover:opacity-90',
  [ButtonVariant.outline]: 'border-orange bg-transparent text-orange hover:bg-orange-subtle',
  [ButtonVariant.destructive]: 'bg-red border-red text-white hover:opacity-90',
  [ButtonVariant.ghost]:
    'border-transparent bg-transparent text-secondary hover:bg-border hover:text-foreground',
  [ButtonVariant.secondary]: 'border-border bg-surface text-foreground hover:bg-border',
  [ButtonVariant.link]:
    'h-auto border-transparent bg-transparent px-0 text-orange underline-offset-2 hover:underline',
}

const sizeClasses: Record<ButtonSize, string> = {
  [ButtonSize.sm]: 'typo-base h-8 px-4',
  [ButtonSize.md]: 'text-base',
  [ButtonSize.lg]: 'h-12 px-5',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = ButtonVariant.primary,
      size = ButtonSize.md,
      loading,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          // base
          'inline-flex h-10 cursor-pointer items-center justify-center gap-2 rounded-xl border px-4 text-lg transition',
          'focus-visible:ring-orange focus-visible:ring-2 focus-visible:outline-none',
          // Неактивная кнопка в макете — сплошная серая, а не приглушённая
          // заливка своего варианта: `opacity-50` давала блёклый оранжевый, по
          // которому не читалось, что кнопка недоступна. `bg-none` и
          // `shadow-none` нужны варианту success — у него заливка градиентом и
          // своя тень, прозрачностью их не погасить.
          'disabled:border-border disabled:bg-border disabled:cursor-not-allowed disabled:bg-none',
          'disabled:text-disabled disabled:shadow-none disabled:hover:opacity-100',
          variantClasses[variant],
          sizeClasses[size],
          className
        )}
        {...props}
      >
        {loading && (
          <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        )}
        {children}
      </button>
    )
  }
)
Button.displayName = 'Button'
